/**
 * Rendu de la fiche facture (modèle normalisé → DOM).
 *
 * Vocabulaire imposé par README §9 : une valeur absente n'est jamais rendue
 * comme « inconnue » ni « 0,00 » ; un champ hors profil est distingué d'un
 * champ simplement non renseigné.
 */
import {
  type ExtractionResult,
  type InvoiceDocument,
  type InvoiceLine,
  type Party,
  PROFILE_RANK,
} from '../lib/model';
import { DOCUMENT_TYPE_FR, PROFILE_LABELS_FR, WARNING_FR } from '../lib/labels.fr';
import { FIELD_DEFS } from '../lib/fields';
import { absent, h } from './dom';
import { fileIcon, warningIcon } from './icons';
import { badgeFor, profileMismatch, profileNotice, renderBadge } from './badge';
import {
  formatBytes,
  formatDate,
  formatIban,
  formatMoney,
  formatPaymentMeans,
  formatQuantity,
  formatTaxCategory,
  formatUnit,
} from './format';

export const ABSENT_IN_FILE = 'Non renseigné dans la facture';
export const PRINT_MENTION = 'Document de lecture — ne remplace pas la facture originale';

type Child = Node | string | null | undefined | false;

/** Libellé d'absence : hors profil ou simplement non renseigné. */
export function absentLabel(bt: string, doc: InvoiceDocument): string {
  const def = FIELD_DEFS.find((field) => field.bt === bt);
  const rank = PROFILE_RANK[doc.profile.profile];
  if (def && rank >= 0 && rank < def.minProfile) {
    return `Non prévu par le profil ${PROFILE_LABELS_FR[doc.profile.profile]}`;
  }
  return ABSENT_IN_FILE;
}

function field(bt: string, label: string, value: string | undefined, doc: InvoiceDocument): HTMLElement {
  const dd = h('dd', null, value ?? absent(absentLabel(bt, doc)));
  if (value) dd.title = value;
  return h(
    'div',
    { class: 'field' },
    h('dt', null, label, ' ', h('span', { class: 'bt', 'aria-hidden': 'true' }, bt)),
    dd,
  );
}

/** Champ libre dont le code BT n'est pas dans le registre (§9 : « valeur + BT »). */
function rawField(bt: string, label: string, value: string | undefined): HTMLElement {
  const dd = h('dd', null, value ?? absent(ABSENT_IN_FILE));
  if (value) dd.title = value;
  return h(
    'div',
    { class: 'field' },
    h('dt', null, label, ' ', h('span', { class: 'bt', 'aria-hidden': 'true' }, bt)),
    dd,
  );
}

function section(id: string, title: string, ...children: Child[]): HTMLElement {
  return h(
    'section',
    { class: 'panel', 'aria-labelledby': id },
    h('h3', { id, class: 'panel__title' }, title),
    ...children,
  );
}

function address(p: Party): string | undefined {
  const parts = [p.address?.line1, p.address?.line2, p.address?.line3, [p.address?.postcode, p.address?.city].filter(Boolean).join(' '), p.address?.country];
  const line = parts.filter((part) => part && String(part).trim() !== '').join(', ');
  return line || undefined;
}

function partyCard(title: string, p: Party, role: string): HTMLElement {
  const lines: Child[] = [];
  if (p.name) lines.push(h('p', { class: 'party__name' }, p.name));
  else lines.push(h('p', { class: 'party__name absent' }, ABSENT_IN_FILE));
  if (p.tradingName && p.tradingName !== p.name) lines.push(h('p', { class: 'party__line' }, p.tradingName));
  if (address(p)) lines.push(h('p', { class: 'party__line' }, address(p) as string));
  if (p.vatId) lines.push(h('p', { class: 'party__line' }, `TVA : ${p.vatId}`));
  if (p.siret) lines.push(h('p', { class: 'party__line' }, `SIRET : ${p.siret}`));
  else if (p.legalRegistrationId && !p.siret) lines.push(h('p', { class: 'party__line' }, `Identifiant légal : ${p.legalRegistrationId}`));
  if (p.electronicAddress?.value) lines.push(h('p', { class: 'party__line' }, p.electronicAddress.value));
  if (p.contact?.name || p.contact?.email || p.contact?.phone) {
    lines.push(
      h('p', { class: 'party__line party__line--muted' }, [p.contact?.name, p.contact?.email, p.contact?.phone].filter(Boolean).join(' · ')),
    );
  }
  return h('div', { class: 'party', 'data-role': role }, h('h4', { class: 'party__title' }, title), ...lines);
}

function lineRow(line: InvoiceLine, index: number): HTMLElement {
  const title = line.description && line.description !== line.name ? `${line.name ?? ''} — ${line.description}` : line.name;
  return h(
    'tr',
    null,
    h('td', { class: 'num' }, line.id ?? String(index + 1)),
    h(
      'td',
      null,
      title ? h('span', { class: 'line__name' }, title) : absent(ABSENT_IN_FILE),
      line.sellerItemId ? h('span', { class: 'line__ref' }, `Réf. ${line.sellerItemId}`) : null,
    ),
    h('td', { class: 'num' }, formatQuantity(line.quantity) ?? absent(ABSENT_IN_FILE)),
    h('td', null, formatUnit(line.unitCode) ?? absent(ABSENT_IN_FILE)),
    h('td', { class: 'num' }, formatMoney(line.unitPrice) ?? absent(ABSENT_IN_FILE)),
    h('td', { class: 'num' }, line.vatRate ? `${line.vatRate.toString().replace('.', ',')} %` : formatTaxCategory(line.vatCategory) ?? absent(ABSENT_IN_FILE)),
    h('td', { class: 'num strong' }, formatMoney(line.lineNetAmount) ?? absent(ABSENT_IN_FILE)),
  );
}

function totalsList(doc: InvoiceDocument): HTMLElement {
  const currency = doc.currency ?? 'EUR';
  const money = (value: InvoiceDocument['totals'][keyof InvoiceDocument['totals']]): string | undefined => formatMoney(value, currency);
  const rows: Array<[string, string, string | undefined, boolean]> = [
    ['BT-106', 'Total des lignes hors taxes', money(doc.totals.lineNet), false],
    ['BT-107', 'Remises globales', money(doc.totals.allowanceTotal), false],
    ['BT-108', 'Majorations globales', money(doc.totals.chargeTotal), false],
    ['BT-109', 'Total hors taxes', money(doc.totals.taxBasis), false],
    ['BT-110', 'Total de TVA', money(doc.totals.taxTotal), false],
    ['BT-111', 'TVA (devise de TVA)', money(doc.totals.taxTotalAccounting), false],
    ['BT-112', 'Total toutes taxes comprises', money(doc.totals.grandTotal), false],
    ['BT-113', 'Déjà payé', money(doc.totals.prepaid), false],
    ['BT-114', 'Arrondi', money(doc.totals.rounding), false],
    ['BT-115', 'Net à payer', money(doc.totals.duePayable), true],
  ];

  const dl = h('dl', { class: 'totals' });
  for (const [bt, label, value, emphasis] of rows) {
    // Les lignes vides (hors BT-115) sont omises : un total absent n'aide pas.
    if (!value && !emphasis) continue;
    dl.appendChild(
      h(
        'div',
        { class: `totals__row${emphasis ? ' totals__row--grand' : ''}` },
        h('dt', null, label, ' ', h('span', { class: 'bt', 'aria-hidden': 'true' }, bt)),
        h('dd', null, value ?? absent(absentLabel(bt, doc))),
      ),
    );
  }
  return dl;
}

function warningsPanel(result: ExtractionResult): HTMLElement {
  if (result.warnings.length === 0) {
    return section('s-attention', "Points d'attention", h('p', { class: 'muted' }, 'Aucun point d’attention détecté.'));
  }
  const list = h('ul', { class: 'warnings' });
  for (const warning of result.warnings) {
    const label = WARNING_FR[warning.code] ?? warning.code;
    list.appendChild(
      h(
        'li',
        { class: `warning warning--${warning.severity}` },
        h('span', { class: 'warning__code' }, label),
        h('span', { class: 'warning__message' }, warning.message),
      ),
    );
  }
  return section('s-attention', "Points d'attention", list);
}

function sourcePanel(result: ExtractionResult): HTMLElement {
  const doc = result.doc;
  let origin: string;
  if (result.source.kind === 'xml') {
    origin = `Fichier XML autonome${result.source.filename ? ` : ${result.source.filename}` : ''}`;
  } else if (result.pdf?.xmlSource === 'af') {
    origin = 'Lu depuis la pièce jointe PDF/A (/AF)';
  } else {
    origin = `Lu depuis ${result.pdf?.xmlFilename ?? 'la pièce jointe du PDF'}`;
  }

  const dl = h('dl', { class: 'kv' });
  const row = (label: string, value: string | undefined): void => {
    if (!value) return;
    dl.appendChild(h('div', { class: 'kv__row' }, h('dt', null, label), h('dd', null, value)));
  };

  row('Syntaxe', doc.syntax === 'CII' ? `CII ${doc.syntaxVersion ?? 'D16B'}` : `UBL ${doc.syntaxVersion ?? '2.1'}`);
  row('Profil (BT-24)', doc.profile.urn ?? undefined);
  row('Taille du document', formatBytes(result.source.byteLength));
  if (result.pdf) {
    row('Pièces jointes', result.pdf.attachmentCount ? result.pdf.attachmentNames.join(', ') : 'aucune');
    row('Profil annoncé par le PDF (XMP)', result.pdf.xmpProfileLevel ?? undefined);
    row('PDF/A', result.pdf.pdfaPart ? `PDF/A-${result.pdf.pdfaPart}${result.pdf.pdfaConformance ?? ''}` : undefined);
    row('Producteur PDF', result.pdf.producer ?? undefined);
  }

  return section(
    's-source',
    'Source',
    h('p', { class: 'origin' }, origin),
    dl,
    h('p', { class: 'muted small' }, 'FactureLire a lu ce document sans le modifier et sans aucune connexion réseau.'),
  );
}

export function renderInvoice(result: ExtractionResult): HTMLElement {
  const doc = result.doc;
  const badge = badgeFor(doc.profile);
  const notice = profileNotice(doc.profile);
  const mismatch = profileMismatch(result.warnings, result.pdf?.xmpProfileLevel ?? null, doc.profile.profile);

  const typeLabel = doc.documentTypeCode ? (DOCUMENT_TYPE_FR[doc.documentTypeCode] ?? 'Document') : 'Document';
  const heading = doc.number ? `${typeLabel} n° ${doc.number}` : typeLabel;

  const head = h(
    'header',
    { class: 'invoice__head' },
    h(
      'div',
      { class: 'badges' },
      renderBadge(badge),
      h('span', { class: `badge badge--neutral badge--doctype` }, typeLabel),
      mismatch
        ? h(
            'button',
            {
              type: 'button',
              class: 'badge-warn',
              'aria-expanded': 'false',
              'aria-controls': 'profile-mismatch',
              title: 'Le PDF et le XML ne déclarent pas le même profil',
            },
            warningIcon(14),
            h('span', { class: 'visually-hidden' }, 'Voir le détail de l’écart de profil'),
          )
        : null,
    ),
    h('h2', { class: 'invoice__title', id: 'invoice-title', tabindex: '-1' }, heading),
    h(
      'p',
      { class: 'invoice__meta' },
      [
        doc.issueDate ? `Émise le ${formatDate(doc.issueDate)}` : null,
        doc.currency ?? null,
        doc.totals.duePayable ? `Net à payer ${formatMoney(doc.totals.duePayable, doc.currency ?? 'EUR')}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    ),
    h(
      'p',
      { class: 'file-badge' },
      fileIcon(13),
      h('span', { class: 'file-badge__name' }, result.source.filename ?? 'document'),
      h('span', { class: 'file-badge__state' }, '· non modifié'),
    ),
  );

  const mismatchDetail = h(
    'div',
    { class: 'mismatch', id: 'profile-mismatch', hidden: true },
    h('p', { class: 'mismatch__title' }, 'Le PDF et le XML ne déclarent pas le même profil.'),
    h(
      'p',
      null,
      `XMP : ${mismatch?.xmpLevel ?? 'non déclaré'} · BT-24 : ${mismatch?.bt24Profile ?? doc.profile.profile}. Le XML fait foi pour tout ce qui est affiché.`,
    ),
  );

  const dates = section(
    's-dates',
    'Dates et références',
    h(
      'dl',
      { class: 'grid' },
      field('BT-2', "Date d'émission", formatDate(doc.issueDate), doc),
      field('BT-9', "Date d'échéance", formatDate(doc.dueDate ?? doc.payment.dueDate), doc),
      field('BT-72', 'Livraison', formatDate(doc.deliveryDate), doc),
      field('BT-5', 'Devise', doc.currency, doc),
      field('BT-10', 'Référence acheteur', doc.buyerReference, doc),
      field('BT-13', 'Commande', doc.orderReference, doc),
      field('BT-14', 'Commande vendeur', doc.salesOrderReference, doc),
      field('BT-12', 'Contrat', doc.contractReference, doc),
    ),
  );

  const parties = section(
    's-parties',
    'Parties',
    h(
      'div',
      { class: 'parties' },
      partyCard('Vendeur', doc.seller, 'seller'),
      partyCard('Acheteur', doc.buyer, 'buyer'),
      doc.payee ? partyCard('Bénéficiaire du paiement', doc.payee, 'payee') : null,
    ),
  );

  const linesTable = h(
    'table',
    { class: 'table' },
    h('caption', { class: 'visually-hidden' }, 'Lignes de facture'),
    h(
      'thead',
      null,
      h(
        'tr',
        null,
        h('th', { scope: 'col' }, 'N°'),
        h('th', { scope: 'col' }, 'Désignation'),
        h('th', { scope: 'col', class: 'num' }, 'Qté'),
        h('th', { scope: 'col' }, 'Unité'),
        h('th', { scope: 'col', class: 'num' }, 'P.U. HT'),
        h('th', { scope: 'col', class: 'num' }, 'TVA'),
        h('th', { scope: 'col', class: 'num' }, 'Total HT'),
      ),
    ),
    h('tbody', null, ...doc.lines.map(lineRow)),
  );

  const lines =
    doc.lines.length > 0
      ? section('s-lines', `Lignes (${doc.lines.length})`, h('div', { class: 'table-wrap' }, linesTable))
      : section(
          's-lines',
          'Lignes',
          h(
            'p',
            { class: 'muted' },
            doc.profile.profile === 'MINIMUM' || doc.profile.profile === 'BASIC_WL'
              ? `Ce profil (${PROFILE_LABELS_FR[doc.profile.profile]}) ne comporte pas de lignes détaillées.`
              : ABSENT_IN_FILE,
          ),
        );

  const taxSection =
    doc.taxes.length > 0
      ? section(
          's-tax',
          'TVA',
          h(
            'div',
            { class: 'table-wrap' },
            h(
              'table',
              { class: 'table' },
              h('caption', { class: 'visually-hidden' }, 'Décomposition de la TVA'),
              h(
                'thead',
                null,
                h(
                  'tr',
                  null,
                  h('th', { scope: 'col' }, 'Catégorie'),
                  h('th', { scope: 'col', class: 'num' }, 'Taux'),
                  h('th', { scope: 'col', class: 'num' }, 'Base HT'),
                  h('th', { scope: 'col', class: 'num' }, 'TVA'),
                  h('th', { scope: 'col' }, 'Exonération'),
                ),
              ),
              h(
                'tbody',
                null,
                ...doc.taxes.map((tax) =>
                  h(
                    'tr',
                    null,
                    h('td', null, formatTaxCategory(tax.category) ?? absent(ABSENT_IN_FILE)),
                    h('td', { class: 'num' }, tax.rate ? `${tax.rate.toString().replace('.', ',')} %` : absent(ABSENT_IN_FILE)),
                    h('td', { class: 'num' }, formatMoney(tax.taxableAmount, doc.currency ?? 'EUR') ?? absent(ABSENT_IN_FILE)),
                    h('td', { class: 'num' }, formatMoney(tax.taxAmount, doc.currency ?? 'EUR') ?? absent(ABSENT_IN_FILE)),
                    h('td', null, tax.exemptionReason ?? tax.exemptionCode ?? absent(ABSENT_IN_FILE)),
                  ),
                ),
              ),
            ),
          ),
        )
      : null;

  const totals = section('s-totals', 'Totaux', totalsList(doc));

  const payment = section(
    's-payment',
    'Paiement',
    h(
      'dl',
      { class: 'grid' },
      field('BT-81', 'Mode de paiement', formatPaymentMeans(doc.payment.meansCode) ?? doc.payment.meansText, doc),
      field('BT-84', 'IBAN', formatIban(doc.payment.iban) as string | undefined, doc),
      field('BT-86', 'BIC', doc.payment.bic, doc),
      field('BT-20', 'Conditions de paiement', doc.payment.terms, doc),
      rawField('BT-83', 'Référence de paiement', doc.payment.remittanceInformation),
      rawField('BT-89', 'Mandat de prélèvement', doc.payment.mandateReference),
    ),
  );

  const notes =
    doc.notes.length > 0
      ? section('s-notes', 'Mentions', h('ul', { class: 'notes' }, ...doc.notes.map((note) => h('li', null, note))))
      : null;

  const footer = h(
    'footer',
    { class: 'invoice__foot' },
    h(
      'div',
      { class: 'invoice__actions no-print' },
      // Seule action proposée : imprimer la fiche. Rien n'écrit dans le fichier.
      h('button', { type: 'button', class: 'button js-print' }, 'Imprimer la fiche'),
    ),
    h('p', { class: 'print-only print-mention' }, PRINT_MENTION),
    h('p', { class: 'muted small no-print' }, 'Fiche de lecture générée localement — le fichier d’origine n’a pas été modifié.'),
  );

  const article = h(
    'article',
    { class: 'invoice', 'aria-labelledby': 'invoice-title' },
    head,
    notice ? h('p', { class: 'notice', role: 'note' }, notice) : null,
    mismatch ? mismatchDetail : null,
    dates,
    parties,
    lines,
    taxSection,
    totals,
    payment,
    notes,
    warningsPanel(result),
    sourcePanel(result),
    footer,
  );

  // Impression de la fiche mise en page (§9.5) : rien d'autre n'est proposé.
  article.querySelector<HTMLButtonElement>('.js-print')?.addEventListener('click', () => {
    window.print();
  });

  // Le bouton ⚠ déplie le détail de l'écart XMP/BT-24.
  const toggle = article.querySelector<HTMLButtonElement>('.badge-warn');
  if (toggle && mismatch) {
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      mismatchDetail.hidden = open;
    });
  }

  return article;
}
